import React from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from '@material-tailwind/react';
import { AlertProvider } from './alert-context';
import { PhoneProvider } from './phone-context';
import QueryProvider from './query-provider';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import type { ReactNode } from 'react';
import AuthenticatedProviders from './authenticated-providers';
import { I18nProvider } from 'src/modules/shared/contexts/i18n/i18n-context';
import { GlobalPhoneProvider } from './global-phone-context';
import { PermissionsProvider } from 'src/modules/shared/contexts/permissions-context';
import { SupportTenantProvider } from 'src/modules/shared/contexts/support-tenant-context';
import { AuthBootstrapProvider } from 'src/modules/shared/contexts/auth-bootstrap-context';
import { restoreAuthRedirectUrl } from 'src/modules/shared/utils/auth/auth-recovery.util';

interface ProvidersProps {
  children: ReactNode;
}

const Providers: React.FC<ProvidersProps> = ({ children }) => {
  const auth0ClientId = import.meta.env.PROD
    ? String(import.meta.env.VITE_AUTH0_CLIENT_ID ?? '')
    : String(import.meta.env.VITE_AUTH0_DEV_CLIENT_ID ?? '');
  const auth0Domain = String(import.meta.env.VITE_AUTH0_DOMAIN ?? '');
  const auth0Audience = String(import.meta.env.VITE_AUTH0_AUDIENCE ?? '');
  const handleAuthRedirect = (appState?: { returnTo?: string }) => {
    restoreAuthRedirectUrl(appState);
  };

  return (
    <ThemeProvider>
      <AlertProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <Auth0Provider
            domain={auth0Domain}
            clientId={auth0ClientId}
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: auth0Audience,
              scope:
                'openid profile email read:current_user update:current_user_metadata offline_access',
            }}
            onRedirectCallback={handleAuthRedirect}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            cacheLocation="localstorage"
          >
            <PhoneProvider>
              <GlobalPhoneProvider>
                <QueryProvider>
                  <AuthBootstrapProvider>
                    <I18nProvider>
                      <PermissionsProvider>
                        <SupportTenantProvider>
                          <AuthenticatedProviders>
                            {children}
                          </AuthenticatedProviders>
                        </SupportTenantProvider>
                      </PermissionsProvider>
                    </I18nProvider>
                  </AuthBootstrapProvider>
                </QueryProvider>
              </GlobalPhoneProvider>
            </PhoneProvider>
          </Auth0Provider>
        </LocalizationProvider>
      </AlertProvider>
    </ThemeProvider>
  );
};

export default Providers;
